/**
 * Inviting somebody into the tenant.
 *
 * @module components/admin/InviteUserForm
 */

import React, { useEffect, useState } from "react";

import {
  fieldForRefusal,
  INVITE_FIELD_IDS,
  type InviteField,
  validateInvite,
} from "@/components/admin/invite-draft";
import type { InviteRequest } from "@/components/admin/useTenantUsers";
import { TENANT_ROLE_OPTIONS } from "@/components/shared/role-labels";
import {
  Button,
  ErrorAlert,
  FormField,
  LinkButton,
  ListRow,
  Modal,
  MutedText,
  SelectField,
  TextField,
  Toggle,
  WarningAlert,
} from "@/components/ui";
import { type FieldProblem, focusFirstProblem, problemsByField } from "@/utils/field-problems";

const EMPTY: InviteRequest = {
  name: "",
  email: "",
  alternateEmails: "",
  role: "member",
  canCreateProgramme: false,
};

export interface InviteUserFormProps {
  /** The dialog is shown only while this is true. */
  open: boolean;
  /** Called for every way out: Cancel, Escape, and a sent invitation. */
  onClose: () => void;
  pending: boolean;
  error: string | null;
  /** Resolves true when the invitation was sent, and the form then clears. */
  onInvite: (request: InviteRequest) => Promise<boolean>;
  /** Named in the directory notice, so it is clear whose directory checks invitees. */
  tenantName: string;
  /**
   * Whether a first Microsoft sign-in has linked the organisation to its
   * directory. The server refuses every invitation until it has.
   */
  directoryBound: boolean;
  /** An operator impersonating, who holds the recovery once they stop. */
  impersonating?: boolean;
  /** Addresses of the organisation's current users, refused before a round trip. */
  existingEmails?: readonly string[];
}

/** Each problem as a link to its field, so the summary is a way back to it. */
function ProblemSummary({
  problems,
}: {
  problems: readonly FieldProblem<InviteField>[];
}): React.ReactElement {
  return (
    <ErrorAlert title="Fix these before sending the invitation:" data-testid="invite-error-summary">
      <ul className="mt-1 list-disc pl-5">
        {problems.map((problem) => (
          <li key={problem.field}>
            <LinkButton
              to={`#${INVITE_FIELD_IDS[problem.field]}`}
              variant="link"
              className="h-auto min-h-0 p-0 text-left underline"
              onClick={(event) => {
                event.preventDefault();
                document.getElementById(INVITE_FIELD_IDS[problem.field])?.focus();
              }}
            >
              {problem.message}
            </LinkButton>
          </li>
        ))}
      </ul>
    </ErrorAlert>
  );
}

/**
 * @example
 * ```tsx
 * <InviteUserForm pending={pending} error={error} onInvite={invite} />
 * ```
 */
export function InviteUserForm({
  open,
  onClose,
  pending,
  error,
  onInvite,
  tenantName,
  directoryBound,
  impersonating = false,
  existingEmails = [],
}: InviteUserFormProps): React.ReactElement {
  const [draft, setDraft] = useState<InviteRequest>(EMPTY);
  const [problems, setProblems] = useState<FieldProblem<InviteField>[]>([]);
  const [lastSent, setLastSent] = useState<InviteRequest | null>(null);
  // A Client Admin can always create programmes, so the switch reports that
  // rather than offering a choice the role has already made.
  const forced = draft.role === "client-admin";
  let blockedReason: string | undefined;
  if (!directoryBound) {
    blockedReason = `Nobody has signed in to ${tenantName} with Microsoft yet`;
  } else if (draft.email.trim().length === 0) {
    blockedReason = "Enter an email address";
  }
  const byField = problemsByField(problems);
  // Read against what was sent, so editing the field does not move the refusal.
  const refusedField = error && lastSent ? fieldForRefusal(error, lastSent) : undefined;
  const fieldError = (field: InviteField): string | undefined =>
    byField[field] ?? (refusedField === field ? (error ?? undefined) : undefined);

  // A refusal naming an address is fixed in that field, so focus goes there.
  useEffect(() => {
    if (refusedField) {
      document.getElementById(INVITE_FIELD_IDS[refusedField])?.focus();
    }
  }, [error, refusedField]);

  const edit = (field: InviteField, value: string): void => {
    setDraft((current) => ({ ...current, [field]: value }));
    setProblems((current) => current.filter((problem) => problem.field !== field));
  };

  const submit = (): void => {
    if (pending || blockedReason) {
      return;
    }
    const found = validateInvite(draft, existingEmails);
    setProblems(found);
    if (found.length > 0) {
      focusFirstProblem(found, (field) => INVITE_FIELD_IDS[field]);
      return;
    }
    const offered = draft;
    setLastSent(draft);
    onInvite(draft).then((sent) => {
      // Anything typed while it was in flight is the next invitation.
      if (sent) {
        setDraft((current) => (current === offered ? EMPTY : current));
        onClose();
      }
    });
  };

  return (
    <Modal open={open} onClose={onClose} title="Invite user" data-testid="invite-user-dialog">
      <form
        className="grid gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {directoryBound ? (
          <MutedText data-testid="invite-directory-state">
            A first sign-in is checked against {tenantName}&apos;s Microsoft directory.
          </MutedText>
        ) : (
          <WarningAlert role="none" data-testid="invite-directory-state">
            Nobody has signed in to {tenantName} with Microsoft yet, so there is no directory to
            check an invitation against. Invitations open once the nominated Client Admin signs in
            with their own Microsoft work account. Impersonating them does not count.{" "}
            {impersonating
              ? `If they cannot sign in, the nomination can move to a colleague. Stop impersonating, then open Manage tenants → ${tenantName} → Reissue invitation.`
              : `If they cannot sign in, a platform administrator can reissue the Client Admin invitation to a colleague from Manage tenants → ${tenantName} → Reissue invitation.`}
          </WarningAlert>
        )}
        <FormField
          label="Name"
          htmlFor="invite-name"
          helpText="How their name appears in workspace assignments."
        >
          <TextField
            id="invite-name"
            value={draft.name}
            onChange={(name) => {
              setDraft((current) => ({ ...current, name }));
            }}
            placeholder="Full name"
          />
        </FormField>
        <FormField
          label="Email"
          htmlFor="invite-email"
          error={fieldError("email")}
          helpText="Their organisational email. Access checks use it to identify them when they sign in."
        >
          <TextField
            id="invite-email"
            value={draft.email}
            onChange={(email) => {
              edit("email", email);
            }}
            placeholder="name@example.edu"
          />
        </FormField>
        <FormField
          label="Alternate emails"
          htmlFor="invite-alternate-emails"
          error={fieldError("alternateEmails")}
          helpText="Optional, comma-separated. A first sign-in from any of these matches this same invitation."
        >
          <TextField
            id="invite-alternate-emails"
            value={draft.alternateEmails}
            onChange={(alternateEmails) => {
              edit("alternateEmails", alternateEmails);
            }}
            placeholder="name@alt-domain.example"
          />
        </FormField>
        <FormField
          label="Role"
          htmlFor="invite-role"
          helpText="Client Admins manage tenant settings. Members work within assigned programme roles."
        >
          <SelectField
            id="invite-role"
            value={draft.role}
            onChange={(role) => {
              setDraft((current) => ({ ...current, role }));
            }}
            options={TENANT_ROLE_OPTIONS}
            data-testid="tenant-invite-user-role"
          />
        </FormField>
        <FormField
          label="Programme creation"
          htmlFor="invite-programme-creation"
          helpText="Whether they can create new programmes in this tenant."
        >
          {/* Not a `label`: the field's own label already names this toggle,
              and a second one for the same control joins the first in the
              name it announces. */}
          <ListRow
            trailing={
              <Toggle
                id="invite-programme-creation"
                size="sm"
                checked={forced || draft.canCreateProgramme}
                onChange={(canCreateProgramme) => {
                  setDraft((current) => ({ ...current, canCreateProgramme }));
                }}
                disabled={forced}
                disabledReason="A client admin may always create programmes."
                data-testid="tenant-invite-programme-creation"
              />
            }
          >
            <span className="text-md">Allow programme creation</span>
          </ListRow>
        </FormField>
        <Button
          type="submit"
          variant="primary"
          className="mt-2"
          loading={pending}
          // `disabled` rather than the native attribute: the suite maps it to
          // `aria-disabled` plus a click guard, and draws the reason as a
          // tooltip a sighted reader sees rather than only announcing it.
          disabled={Boolean(blockedReason)}
          disabledReason={blockedReason}
        >
          Invite user
        </Button>
        {problems.length > 0 ? <ProblemSummary problems={problems} /> : null}
        {/* A refusal naming an address already sits on its field. */}
        {error && !refusedField ? (
          <ErrorAlert data-testid="invite-error">{error}</ErrorAlert>
        ) : null}
      </form>
    </Modal>
  );
}
